import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import brandRoles from 'utils/brandRoles';
import { FONT, LineIcon, TextButton } from './ui';

/**
 * Feste Statuszeile zum Mailversand eines Updates (nur fuer den Initiator).
 * Bewusst zurueckhaltend: farbiges Symbol, neutraler Text, Aktionen als Links.
 * Farbe ist nie das einzige Signal, es gibt immer Symbol und Satz.
 */

const formatCount = (n) => Number(n || 0).toLocaleString('de-DE');

// Ab diesem Anteil nicht erreichter Adressen wird der Ton deutlicher.
const WARN_SHARE = 0.05;

const SYMBOL_COLORS = {
  hinweis: brandRoles.hinweisSymbol,
  erfolg: brandRoles.erfolgSymbol,
  warnung: brandRoles.warnungSymbol,
  fehler: brandRoles.fehlerSymbol,
};

const mails = (n) => (n === 1 ? '1 E-Mail' : `${formatCount(n)} E-Mails`);

// Kurze Systemmeldungen, keine ganzen Saetze.
function describe(status) {
  const { total, pending, failed } = status;
  const reached = status.sent + status.delivered;

  if (pending > 0) {
    return {
      tone: 'hinweis',
      icon: 'uhr',
      text: `E-Mail-Versand läuft: ${formatCount(total - pending)} von ${formatCount(
        total
      )}`,
    };
  }
  if (failed === 0) {
    return { tone: 'erfolg', icon: 'haken', text: `${mails(reached)} verschickt` };
  }
  if (reached === 0) {
    return { tone: 'fehler', icon: 'kreuz', text: 'E-Mails konnten nicht verschickt werden' };
  }
  return {
    tone: failed / total > WARN_SHARE ? 'warnung' : 'hinweis',
    icon: 'achtung',
    text: `${formatCount(reached)} von ${mails(total)} verschickt · ${formatCount(
      failed
    )} nicht zustellbar`,
  };
}

function DeliveryStatus({ updateId, status, onResend }) {
  const [showFailures, setShowFailures] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState(false);

  if (!status || !status.total) return null;

  const { tone, icon, text } = describe(status);
  const hasFailures = status.pending === 0 && status.failed > 0;
  const canResend = hasFailures && status.resends_left !== 0;

  const handleResend = () => {
    setResending(true);
    setResendError(false);
    Promise.resolve(onResend(updateId))
      .catch(() => setResendError(true))
      .finally(() => setResending(false));
  };

  return (
    <Wrapper role="status">
      <div className="line">
        <span className="symbol" style={{ color: SYMBOL_COLORS[tone] }}>
          <LineIcon name={icon} size={16} />
        </span>
        <span className="text">
          {text}
          {hasFailures && (
            <span className="actions">
              {canResend && (
                <TextButton
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? 'Wird angestoßen …' : 'Erneut senden'}
                </TextButton>
              )}
              <TextButton
                type="button"
                onClick={() => setShowFailures(!showFailures)}
                aria-expanded={showFailures}
              >
                {showFailures ? 'Gründe ausblenden' : 'Gründe anzeigen'}
              </TextButton>
            </span>
          )}
        </span>
      </div>

      {resendError && (
        <div className="note">
          Das hat nicht geklappt. Versuch es in ein paar Minuten noch einmal.
        </div>
      )}

      {hasFailures && showFailures && (
        <ul className="failures">
          {(status.failure_reasons || []).map((f) => (
            <li key={f.reason}>
              <span className="count">{f.count} ×</span>
              <span className="reason">{f.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </Wrapper>
  );
}

DeliveryStatus.propTypes = {
  updateId: PropTypes.number.isRequired,
  status: PropTypes.shape({
    total: PropTypes.number,
    pending: PropTypes.number,
    sent: PropTypes.number,
    delivered: PropTypes.number,
    failed: PropTypes.number,
    resends_left: PropTypes.number,
    // Aggregated per reason, never recipient addresses
    failure_reasons: PropTypes.arrayOf(
      PropTypes.shape({ reason: PropTypes.string, count: PropTypes.number })
    ),
  }),
  onResend: PropTypes.func.isRequired,
};

export default DeliveryStatus;

const Wrapper = styled.div`
  margin-top: 12px;
  font-family: ${FONT};
  font-size: 13px;
  line-height: 20px;
  color: ${brandRoles.neutral700};

  .line {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .symbol {
    flex: 0 0 16px;
    display: inline-flex;
    margin-top: 2px;
  }

  .actions {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0 12px;
    margin-left: 8px;

    button {
      font-size: 13px;
      line-height: 20px;
    }
  }

  .note {
    margin: 4px 0 0 24px;
  }

  .failures {
    margin: 8px 0 0 24px;
    padding: 4px 12px;
    list-style: none;
    max-height: 220px;
    overflow-y: auto;
    background: ${brandRoles.neutral50};
    border: 1px solid ${brandRoles.neutral200};
    border-radius: 8px;

    li {
      display: flex;
      align-items: baseline;
      gap: 6px;
      padding: 6px 0;
      border-top: 1px solid ${brandRoles.neutral200};

      &:first-of-type {
        border-top: 0;
      }
    }

    .count {
      flex: 0 0 auto;
      color: ${brandRoles.neutral500};
      font-variant-numeric: tabular-nums;
    }

    .reason {
      color: ${brandRoles.neutral800};
    }
  }
`;
